import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import useAuthStore from '../stores/authStore';
import useNotificationStore from '../stores/notificationStore';
import ErrorBoundary from './ErrorBoundary';

// MUI Icons
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SettingsIcon  from '@mui/icons-material/Settings';
import NotificationsIcon from '@mui/icons-material/Notifications';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import DashboardIcon  from '@mui/icons-material/Dashboard';
import AccessTimeIcon  from '@mui/icons-material/AccessTime';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import FolderIcon from '@mui/icons-material/Folder';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import BarChartIcon from '@mui/icons-material/BarChart';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import LockIcon from '@mui/icons-material/Lock';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import MenuIcon from '@mui/icons-material/Menu';
import { usersAPI } from '../services/api';

const Layout = ({ children }) => {
    const navigate = useNavigate();
    const location = useLocation();
    
    const { 
        user, logout, isAdmin, isManager, 
        theme, setTheme, 
        notificationsEnabled, setNotificationsEnabled 
    } = useAuthStore();
    const { notifications, unreadCount, fetchNotifications, markAsRead, markAllAsRead, clearAllNotifications } = useNotificationStore();

    // Maintenance Mode State
    const [isMaintenance, setIsMaintenance] = useState(false);
    const [recovered, setRecovered] = useState(false); // brief "back online" flash

    useEffect(() => {
        const onMaintenance = () => {
            setIsMaintenance(true);
            setRecovered(false);
        };
        const onRecovered = () => {
            setRecovered(true);
            // Keep the overlay visible for 3 s with a "back online" message, then hide
            setTimeout(() => {
                setIsMaintenance(false);
                setRecovered(false);
            }, 3000);
        };
        window.addEventListener('app:maintenance', onMaintenance);
        window.addEventListener('app:recovered', onRecovered);
        return () => {
            window.removeEventListener('app:maintenance', onMaintenance);
            window.removeEventListener('app:recovered', onRecovered);
        };
    }, []);

    // UI State
    const [showProfileMenu, setShowProfileMenu] = useState(false);
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [showMobileSidebar, setShowMobileSidebar] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [showNotifications, setShowNotifications] = useState(false);
    const [settingsView, setSettingsView] = useState('main');

    const [passwordData, setPasswordData] = useState({
        oldPassword: '',
        newPassword: '',
        confirmPassword: ''
    });

    useEffect(() => {
        if (user) {
            fetchNotifications();
            const interval = setInterval(fetchNotifications, 30000);
            return () => clearInterval(interval);
        }
    }, [user, fetchNotifications]);

    useEffect(() => {
        if (!showSettings) setSettingsView('main');
    }, [showSettings]);

    useEffect(() => {
        document.body.className = theme === "dark" ? "dark-theme" : "light-theme";
    }, [theme]);

    const handlePasswordUpdate = async (e) => {
        e.preventDefault();
        if (passwordData.newPassword !== passwordData.confirmPassword){
            alert("New passwords do not match!");
            return;
        }
        
        try {
            await usersAPI.updateMyPassword({
                old_password: passwordData.oldPassword,
                new_password: passwordData.newPassword
            });
            alert("Password successfully updated. Please use it next time you login.");
            setSettingsView('main');
            setPasswordData({oldPassword: '', newPassword:'', confirmPassword:''});
        } catch (error) {
            console.error(error);
            const errorMsg = error.response?.data?.detail || "Failed to update password. Check if your current password is correct and your new password is at least 6 characters.";
            alert(errorMsg);
        }
    };

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    useEffect(() => {
        // Close mobile sidebar on route change
        setShowMobileSidebar(false);
    }, [location.pathname]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (
                !e.target.closest(".nav-icon") &&
                !e.target.closest(".settings-dropdown") &&
                !e.target.closest(".notif-dropdown") &&
                !e.target.closest(".profile-trigger")
            ) {
                setShowSettings(false);
                setShowNotifications(false);
                setShowProfileMenu(false);
            }
        };

        document.addEventListener("click", handleClickOutside);
        return () => document.removeEventListener("click", handleClickOutside);
    }, []);

    // Dynamic Navigation System integrated with React Router
    const navItems = [
        { path: '/dashboard', icon: DashboardIcon, label: 'Dashboard' },
        { path: '/timesheets', icon: AccessTimeIcon, label: 'Timesheet' },
        { path: '/projects', icon: FolderIcon, label: 'Projects' },
        { path: '/reports', icon: BarChartIcon, label: 'Reports' },
        ...(isAdmin() || isManager() ? [
            { path: '/approvals', icon: AssignmentTurnedInIcon, label: 'Approvals' }
        ] : []),
        ...(isAdmin() ? [
            { path: '/users', icon: GroupAddIcon, label: 'User Provisioning' },
            { path: '/system-settings', icon: SettingsIcon, label: 'System Settings' }
        ] : [])
    ];


    return(
        <div className={`app-wrapper ${theme}`}>

            {/* ─── Maintenance / Under-Construction Overlay ─────────────────── */}
            {isMaintenance && (
                <div className="maintenance-overlay">
                    <div className="maintenance-card">
                        {recovered ? (
                            <>
                                <div className="maintenance-icon recovered">
                                    <span style={{ fontSize: '40px' }}>✅</span>
                                </div>
                                <div className="maintenance-title" style={{ color: 'var(--success, #16a34a)' }}>We're back online!</div>
                                <div className="maintenance-subtitle">The server has recovered. Resuming normally…</div>
                            </>
                        ) : (
                            <>
                                <div className="maintenance-icon">
                                    <span style={{ fontSize: '40px' }}>🔧</span>
                                    <div className="maintenance-pulse" />
                                </div>
                                <div className="maintenance-title">Under Maintenance</div>
                                <div className="maintenance-subtitle">
                                    We're applying an update to our servers.<br />
                                    This usually takes less than a minute — hang tight!
                                </div>
                                <div className="maintenance-dots">
                                    <span /><span /><span />
                                </div>
                                <div className="maintenance-status-row">
                                    <span className="maintenance-status-dot" />
                                    Checking server status every 15 seconds…
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
            <nav className="top-navbar">
                <div className="left-navbar">
                    <button className="mobile-menu-btn" onClick={() => setShowMobileSidebar(true)}>
                        <MenuIcon />
                    </button>
                    <div className="logo-container" onClick={() => navigate('/dashboard')} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                        <img src="/tickmarks-logo.png" alt="Tickmarks" style={{ height: '32px' }} />
                    </div>
                </div>

                <div className="nav-controls">
                    <div className="nav-item">
                        <span 
                            className="nav-icon"
                            onClick={(e) => {
                                e.stopPropagation();
                                setShowSettings(!showSettings);
                                setShowNotifications(false);
                                setShowProfileMenu(false);
                            }}
                        >
                            <SettingsIcon/>
                        </span>
                        
                        {showSettings && (
                            <div className="settings-dropdown" onClick={(e) => e.stopPropagation()}>
                                {settingsView === 'main' &&(
                                    <>
                                    <div className="menu-item">
                                        <span>Notifications</span>
                                        <div onClick={(e) =>{ e.stopPropagation();
                                        setNotificationsEnabled(!notificationsEnabled);
                                        }} className="pointer">
                                            {notificationsEnabled ? <ToggleOnIcon color="primary"/> : <ToggleOffIcon color="action"/>}
                                        </div>
                                    </div>
                                    <div className="menu-item pointer" onClick={() => setSettingsView('theme')}>
                                        <span>Theme</span>
                                        <ChevronRightIcon fontSize="small"/>   
                                    </div>
                                    <div className="menu-item pointer" onClick={() => setSettingsView('security')}>
                                        <span><LockIcon fontSize="small"/>Security</span>
                                        <ChevronRightIcon fontSize="small"/>
                                    </div>
                                    {isAdmin() && (
                                        <div className="menu-item pointer" onClick={() => { setShowSettings(false); navigate('/system-settings'); }}>
                                            <span><SettingsIcon fontSize="small"/> System Settings</span>
                                            <ChevronRightIcon fontSize="small"/>
                                        </div>
                                    )}
                                    </>
                                )}

                                {settingsView === 'theme' && (
                                    <div className="sub-menu-container">
                                        <div className="menu-header" onClick={() => setSettingsView('main')}>
                                            <ChevronLeftIcon/> <span>Theme</span>
                                        </div>
                                        <div className="menu-item pointer" onClick={() => setTheme('light')}>
                                            <span>Light</span>
                                            {theme === 'light' ? <RadioButtonCheckedIcon color="primary" fontSize="small"/> 
                                            : <RadioButtonUncheckedIcon fontSize="small"/>}
                                        </div>
                                        <div className="menu-item pointer" onClick={() => setTheme('dark')}>
                                            <span>Dark</span>
                                             {theme === 'dark' ? <RadioButtonCheckedIcon color="primary" fontSize="small"/> 
                                            : <RadioButtonUncheckedIcon fontSize="small"/>}
                                        </div>
                                    </div>
                                )}
                                                    
                                {settingsView === 'security' && (
                                    <div className="sub-menu-container">
                                        <div className="menu-header" onClick={() => setSettingsView('main')}>
                                            <ChevronLeftIcon/> <span>Security</span>
                                        </div>
                                        <form className="security-form" onSubmit={handlePasswordUpdate}>
                                            <input type="password" placeholder="Old password" value={passwordData.oldPassword} onChange={(e) => setPasswordData({...passwordData, oldPassword: e.target.value})} />
                                            <input type="password" placeholder="New password" value={passwordData.newPassword} onChange={(e) => setPasswordData({...passwordData, newPassword:e.target.value})} />
                                            <input type="password" placeholder="Confirm password" value={passwordData.confirmPassword} onChange={(e) => setPasswordData({...passwordData,confirmPassword:e.target.value})} />
                                            <button type="submit" className="update-btn">Update password</button>
                                        </form>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>


                    <div className="nav-item">
                        <span 
                            className="nav-icon"
                            onClick={(e) => {
                                e.stopPropagation();
                                setShowNotifications(prev => !prev);
                                setShowSettings(false);
                                setShowProfileMenu(false);
                                if (!showNotifications && unreadCount > 0) markAllAsRead();
                            }}
                        >
                            <NotificationsIcon/>
                            {unreadCount > 0 && <span className="notification-badge-small">{unreadCount}</span>}
                        </span>
                        
                        {showNotifications && (
                            <div className="notif-dropdown" onClick={(e)=> e.stopPropagation()}>
                                <div className="dropdown-header">
                                    <span>Notifications</span>
                                    {notifications.length > 0 && (
                                        <div style={{ display: 'flex', gap: '10px' }}>
                                            <span 
                                                className="pointer" 
                                                style={{ fontSize: '12px', color: '#0d9488', textDecoration: 'underline' }}
                                                onClick={() => {
                                                    navigate('/notifications');
                                                    setShowNotifications(false);
                                                }}
                                            >
                                                See All
                                            </span>
                                            <button className="clear-btn" onClick={() => clearAllNotifications()}>
                                                Clear All
                                            </button>
                                        </div>
                                    )}
                                </div>
                                <div className="notif-list">
                                    {notifications.length > 0 ? (
                                        notifications.map((n) => (
                                            <div key={n.id} className="notif-item pointer" onClick={() => {
                                                if (!n.is_read) markAsRead(n.id);
                                                if (n.link) navigate(n.link);
                                                setShowNotifications(false);
                                            }}>
                                                <div style={{ flex: 1 }}>
                                                    <p style={{ fontWeight: 600, margin: 0, opacity: n.is_read ? 0.6 : 1 }}>{n.title}</p>
                                                    <p style={{ fontSize: '12px', color: 'var(--text-secondary)', margin: '4px 0 0 0', opacity: n.is_read ? 0.6 : 1 }}>{n.message}</p>
                                                </div>
                                                <ChevronRightIcon fontSize="small" style={{ opacity: 0.4 }}/>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="empty-notif">No new notifications</div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    
                    <div className="nav-item">
                        <div 
                            className="profile-trigger"
                            onClick={(e) => {
                                e.stopPropagation();
                                setShowProfileMenu(prev => !prev);
                                setShowSettings(false);
                                setShowNotifications(false);
                            }}
                        >
                            <div className="avatar-circle">
                                <AccountCircleIcon/>
                            </div>

                            {showProfileMenu && (
                                <div className="Profile-dropdown">
                                    <ul>
                                        <li onClick={() => { navigate('/profile'); setShowProfileMenu(false); }} style={{ cursor: 'pointer' }}>
                                            <AccountCircleIcon/>
                                            Profile
                                        </li>
                                        <li onClick={handleLogout} className="logout-item">
                                            <LogoutIcon/>
                                            Logout
                                        </li>
                                    </ul>
                                </div>
                            )}
                        </div>
                    </div>

                </div>
            </nav>

            <div className="app-body">
                {showMobileSidebar && <div className="sidebar-overlay" onClick={() => setShowMobileSidebar(false)} style={{ zIndex: 1999 }} />}
                <aside className={`sidebar-container ${isCollapsed ? 'collapsed' : ''} ${showMobileSidebar ? 'mobile-visible' : ''}`}>
                    <button 
                        className="sidebar-toggle-handle" 
                        onClick={() => setIsCollapsed(!isCollapsed)}
                        title={isCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
                    >
                        {isCollapsed ? <ChevronRightIcon fontSize="small"/> : <ChevronLeftIcon fontSize="small"/>}
                    </button>
                    
                    <ul className="menu-list">
                        {navItems.map((item) => {
                            const Icon = item.icon;
                            const isActive = location.pathname.startsWith(item.path);
                            return (
                                <li 
                                    key={item.path}
                                    className={isActive ? 'active' : ''}
                                    onClick={() => navigate(item.path)}
                                    title={isCollapsed ? item.label : ''}
                                >
                                    <Icon/>
                                    {!isCollapsed && <span>{item.label}</span>}
                                </li>
                            );
                        })}
                    </ul>
                </aside>

                <main className="main-content">
                    <ErrorBoundary fallback={<div className="error-text">Failed to load content chunk!</div>}>
                        {children}
                    </ErrorBoundary>
                </main>
            </div>
        </div>
    );
};

export default Layout;